import { type ReactNode, useEffect, useState } from "react";
import { NavIcon, type NavIconName } from "./nav-icon";

function readSaved(key: string): boolean | null {
  try {
    const saved = localStorage.getItem(key);
    return saved === null ? null : saved === "open";
  } catch {
    return null;
  }
}

/**
 * A collapsible sidebar group. Expanded, the heading is a quiet label with a
 * chevron (Linear-style); on the collapsed rail it becomes the group's icon,
 * and opening it expands the rail. Browser-only preferences never grant
 * access to module content.
 */
export function SidebarSection({
  id,
  label,
  icon,
  defaultOpen = false,
  activeKey,
  containsActive = false,
  revealOnly = false,
  onOpen,
  children,
}: {
  id: string;
  label: string;
  /** Shown on the collapsed rail, where the heading text is hidden. */
  icon: NavIconName;
  /** Used until the operator folds or opens the group themselves. */
  defaultOpen?: boolean;
  /** A new active URL reveals its parent without preventing manual folding. */
  activeKey?: string;
  /** Marks the group on the collapsed rail when the current page is inside it. */
  containsActive?: boolean;
  /** Collapsed rail: a click only ever opens (and expands the rail). */
  revealOnly?: boolean;
  onOpen: () => void;
  children: ReactNode;
}) {
  const key = `backchannel.tax.sidebar.section.${id}`;
  // null follows `defaultOpen` (for example, Governança opens while work is pending).
  const [preference, setPreference] = useState<boolean | null>(() =>
    activeKey ? true : readSaved(key),
  );
  useEffect(() => {
    if (activeKey) setPreference(true);
  }, [activeKey]);
  const open = preference ?? defaultOpen;

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: native summary activates click with Enter and Space; duplicating key handlers toggles twice.
    <details
      className="sidenav-group"
      data-section={id}
      data-contains-active={containsActive ? "true" : undefined}
      open={open}
      onClick={(event) => {
        if (
          !(event.target instanceof Element) ||
          event.target.closest("summary") !== event.currentTarget.querySelector(":scope > summary")
        )
          return;
        event.preventDefault();
        const next = revealOnly || !open;
        setPreference(next);
        if (next) onOpen();
        try {
          localStorage.setItem(key, next ? "open" : "closed");
        } catch {
          /* Storage is optional. */
        }
      }}
    >
      <summary className="sidenav-group__summary" title={label}>
        <span className="sidenav-group__icon" aria-hidden="true">
          <NavIcon name={icon} />
        </span>
        <span className="sidenav-group__label">{label}</span>
        <span className="sidenav-group__chevron" aria-hidden="true">
          <NavIcon name="chevron" />
        </span>
      </summary>
      <div className="sidenav-group__items">{children}</div>
    </details>
  );
}
